import React, { useState, useEffect } from 'react';
import { config } from '../../config';

interface Competition {
  id: string;
  name: string;
  competition_type?: string;
  tier: string;
  season_start: string;
  season_end: string;
  package_type_id?: string;
  tshirt_cost: number;
  team_registration_fee: number;
  tournament_registration_fee: number;
  other_costs: number;
  other_costs_description?: string;
  status: string;
}

interface PackageType {
  id: string;
  name: string;
  tier: string;
  session_count: number;
  price: number;
}

interface CompetitionFormProps {
  competition: Competition | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const CompetitionForm: React.FC<CompetitionFormProps> = ({
  competition,
  onSuccess,
  onCancel
}) => {
  const [formData, setFormData] = useState({
    name: '',
    competition_type: '',
    tier: 'foundations',
    season_start: '',
    season_end: '',
    package_type_id: '',
    tshirt_cost: 0.00,
    team_registration_fee: 0.00,
    tournament_registration_fee: 0.00,
    other_costs: 0.00,
    other_costs_description: '',
    status: 'upcoming'
  });

  const [availablePackages, setAvailablePackages] = useState<PackageType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch package types for the selected tier
  useEffect(() => {
    const fetchPackageTypes = async () => {
      try {
        const token = localStorage.getItem('cognitoToken') || '';
        const response = await fetch(`${config.BACKEND_URL}/api/package-types?tier=${formData.tier}&status=active`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setAvailablePackages(data);
        }
      } catch (err) {
        console.error('Error fetching package types:', err);
      }
    };

    fetchPackageTypes();
  }, [formData.tier]);

  useEffect(() => {
    if (competition) {
      setFormData({
        name: competition.name,
        competition_type: competition.competition_type || '',
        tier: competition.tier,
        season_start: competition.season_start,
        season_end: competition.season_end,
        package_type_id: competition.package_type_id || '',
        tshirt_cost: Number(competition.tshirt_cost) || 0.00,
        team_registration_fee: Number(competition.team_registration_fee) || 0.00,
        tournament_registration_fee: Number(competition.tournament_registration_fee) || 0.00,
        other_costs: Number(competition.other_costs) || 0.00,
        other_costs_description: competition.other_costs_description || '',
        status: competition.status
      });
    }
  }, [competition]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: ['tshirt_cost', 'team_registration_fee', 'tournament_registration_fee', 'other_costs'].includes(name)
        ? parseFloat(value) || 0
        : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validation
    if (!formData.package_type_id) {
      setError('Please select a mandatory session pack');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('cognitoToken') || '';
      const url = competition
        ? `${config.BACKEND_URL}/api/competitions/${competition.id}`
        : `${config.BACKEND_URL}/api/competitions`;
      
      const method = competition ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert(`✅ Competition ${competition ? 'updated' : 'created'} successfully!`);
        onSuccess();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to save competition');
      }
    } catch (err: any) {
      console.error('Error saving competition:', err);
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Get selected package details
  const selectedPackage = availablePackages.find(pkg => pkg.id === formData.package_type_id);

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content competition-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{competition ? '✏️ Edit Competition' : '➕ Create Competition'}</h2>
          <button className="close-button" onClick={onCancel}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="competition-form">
          {error && (
            <div className="error-message" style={{
              padding: '12px',
              backgroundColor: '#ffebee',
              color: '#c62828',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              ❌ {error}
            </div>
          )}

          {/* Basic Information */}
          <div className="form-section">
            <h3 className="section-title">Basic Information</h3>
            
            <div className="form-group">
              <label>Competition Name <span className="required">*</span></label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., FLL Explore 2025-2026"
                required
                className="form-control"
              />
              <small className="form-hint">Include the season year for clarity</small>
            </div>

            <div className="form-group">
              <label>Competition Type</label>
              <select
                name="competition_type"
                value={formData.competition_type}
                onChange={handleChange}
                className="form-control"
              >
                <option value="">-- Select Type --</option>
                <option value="fll_explore">FLL Explore</option>
                <option value="fll_challenge">FLL Challenge</option>
                <option value="chess_club">Chess Club</option>
                <option value="other">Other</option>
              </select>
              <small className="form-hint">Optional: categorize the competition</small>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Tier <span className="required">*</span></label>
                <select
                  name="tier"
                  value={formData.tier}
                  onChange={handleChange}
                  required
                  className="form-control"
                >
                  <option value="foundations">🌱 Foundations (K-5)</option>
                  <option value="innovators">🚀 Innovators (Grades 6-8)</option>
                  <option value="pioneers">🌟 Pioneers (Grades 9-12+)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Status <span className="required">*</span></label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                  className="form-control"
                >
                  <option value="upcoming">Upcoming</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>

          {/* Season Information */}
          <div className="form-section">
            <h3 className="section-title">Season Information</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label>Season Start Date <span className="required">*</span></label>
                <input
                  type="date"
                  name="season_start"
                  value={formData.season_start}
                  onChange={handleChange}
                  required
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>Season End Date <span className="required">*</span></label>
                <input
                  type="date"
                  name="season_end"
                  value={formData.season_end}
                  onChange={handleChange}
                  required
                  className="form-control"
                />
              </div>
            </div>
          </div>

          {/* Pricing Structure */}
          <div className="form-section">
            <h3 className="section-title">Pricing Structure</h3>
            
            <div className="form-group">
              <label>Mandatory Session Pack <span className="required">*</span></label>
              <select
                name="package_type_id"
                value={formData.package_type_id}
                onChange={handleChange}
                required
                className="form-control"
              >
                <option value="">-- Select Session Pack --</option>
                {availablePackages.map(pkg => (
                  <option key={pkg.id} value={pkg.id}>
                    {pkg.name} - {pkg.session_count} sessions - ${Number(pkg.price).toFixed(2)}
                  </option>
                ))}
              </select>
              <small className="form-hint">
                Required session pack for this competition. {selectedPackage && `Selected: ${selectedPackage.session_count} sessions for $${Number(selectedPackage.price).toFixed(2)}`}
              </small>
            </div>

            <h4 style={{ marginTop: '20px', marginBottom: '12px', fontSize: '16px', color: '#444' }}>
              Individual Fees (per student)
            </h4>
            
            <div className="form-group">
              <label>T-Shirt Cost</label>
              <div className="input-with-prefix">
                <span className="input-prefix">$</span>
                <input
                  type="number"
                  name="tshirt_cost"
                  value={formData.tshirt_cost}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="form-control"
                  style={{ paddingLeft: '30px' }}
                />
              </div>
              <small className="form-hint">Cost of team t-shirt per student</small>
            </div>

            <h4 style={{ marginTop: '20px', marginBottom: '12px', fontSize: '16px', color: '#444' }}>
              Shared Fees (split across team members)
            </h4>

            <div className="form-group">
              <label>Team Registration Fee</label>
              <div className="input-with-prefix">
                <span className="input-prefix">$</span>
                <input
                  type="number"
                  name="team_registration_fee"
                  value={formData.team_registration_fee}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="form-control"
                  style={{ paddingLeft: '30px' }}
                />
              </div>
              <small className="form-hint">One-time team registration fee (split among team members)</small>
            </div>

            <div className="form-group">
              <label>Tournament Registration Fee</label>
              <div className="input-with-prefix">
                <span className="input-prefix">$</span>
                <input
                  type="number"
                  name="tournament_registration_fee"
                  value={formData.tournament_registration_fee}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="form-control"
                  style={{ paddingLeft: '30px' }}
                />
              </div>
              <small className="form-hint">Tournament entry fee (split among team members)</small>
            </div>

            <div className="form-group">
              <label>Other Shared Costs</label>
              <div className="input-with-prefix">
                <span className="input-prefix">$</span>
                <input
                  type="number"
                  name="other_costs"
                  value={formData.other_costs}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="form-control"
                  style={{ paddingLeft: '30px' }}
                />
              </div>
              <small className="form-hint">Any additional shared costs</small>
            </div>

            <div className="form-group">
              <label>Other Costs Description</label>
              <textarea
                name="other_costs_description"
                value={formData.other_costs_description}
                onChange={handleChange}
                className="form-control"
                rows={3}
                placeholder="e.g., Competition materials, team supplies, transportation"
              />
              <small className="form-hint">Describe what the other costs cover</small>
            </div>
          </div>

          {/* Cost Summary */}
          {selectedPackage && (
            <div className="form-section" style={{ backgroundColor: '#f0f8ff', borderLeft: '4px solid #2196F3' }}>
              <h3 className="section-title">Cost Summary (Example: Team of 5)</h3>
              <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                <div><strong>Per Student:</strong></div>
                <div>• Session Pack ({selectedPackage.session_count} sessions): ${Number(selectedPackage.price).toFixed(2)}</div>
                <div>• T-Shirt: ${formData.tshirt_cost.toFixed(2)}</div>
                <div>• Team Registration Share: ${(formData.team_registration_fee / 5).toFixed(2)}</div>
                <div>• Tournament Registration Share: ${(formData.tournament_registration_fee / 5).toFixed(2)}</div>
                <div>• Other Costs Share: ${(formData.other_costs / 5).toFixed(2)}</div>
                <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #ccc', fontWeight: 'bold' }}>
                  <strong>Total per Student (team of 5): ${(
                    Number(selectedPackage.price) +
                    formData.tshirt_cost +
                    (formData.team_registration_fee / 5) +
                    (formData.tournament_registration_fee / 5) +
                    (formData.other_costs / 5)
                  ).toFixed(2)}</strong>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="form-actions" style={{ 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'flex-end',
            marginTop: '24px',
            paddingTop: '20px',
            borderTop: '1px solid #e0e0e0'
          }}>
            <button
              type="button"
              onClick={onCancel}
              className="btn-secondary"
              disabled={loading}
              style={{
                padding: '10px 24px',
                fontSize: '14px',
                backgroundColor: '#f5f5f5',
                color: '#666',
                border: 'none',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
              style={{
                padding: '10px 24px',
                fontSize: '14px',
                backgroundColor: loading ? '#ccc' : '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Saving...' : competition ? 'Update Competition' : 'Create Competition'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompetitionForm;
